import React, { useState, useEffect } from 'react';
import { FormControl, FormLabel, TextField, FormHelperText, Grid, Button } from '@mui/material';
import useStyles from '../useStyles';
import PropTypes from 'prop-types';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

function Step2({
  answers,
  setAnswers,
  getSeekAdBrandingData,
  setGetSeekAdBrandingData,
  apiKey,
  jobAdId,
  activeError,
  requiredRefs,
  maxBulletPoints,
  brandingEnabled
}) {
  const [loadingSeekAdBranding, setLoadingSeekAdBranding] = useState(true);
  const [seekBrandindInfo, setSeekBrandindInfo] = useState(null);
  const classes = useStyles();
  const shortDescriptionLength = 150;

  const getSeekAdType = async (url, nextPage) => {
    !nextPage && setLoadingSeekAdBranding(true);
    try {
      const { data } = await axios.get(url, {
        headers: { 'X-api-authenticate': apiKey }
      });
      setGetSeekAdBrandingData(
        nextPage
          ? [...getSeekAdBrandingData, ...data.data.advertisementBrandings.edges]
          : data.data.advertisementBrandings.edges
      );
      setSeekBrandindInfo(data.data.advertisementBrandings.pageInfo);
      if (!answers.branding_id)
        setAnswers({
          ...answers,
          branding_id: data.data.advertisementBrandings.edges[0].node.id.value
        });
      setLoadingSeekAdBranding(false);
    } catch (e) {
      setGetSeekAdBrandingData(null);
      setLoadingSeekAdBranding(false);
    }
  };

  const setStandoutBullet = (index, value) => {
    const standoutBullets = answers.standout_bullets;
    standoutBullets[index] = value;
    setAnswers({ ...answers, standout_bullets: standoutBullets });
  };

  useEffect(() => {
    if (!getSeekAdBrandingData) {
      getSeekAdType(`/api/v4/seek/job_ads/${jobAdId}/brands`, false);
    } else setLoadingSeekAdBranding(false);
  }, []);

  return (
    <div className={classes.stepContainer}>
      <h4>Job details</h4>

      <FormControl
        ref={requiredRefs.description}
        fullWidth={true}
        className={`${classes.formControl} ${classes.large}`}
      >
        <FormLabel required={true} className={classes.label}>
          Short Description
        </FormLabel>
        <TextField
          error={activeError === requiredRefs.description}
          multiline={true}
          className={`${classes.textField} ${classes.textBox}`}
          variant="outlined"
          value={answers.description}
          onChange={(e) =>
            e.target.value.length <= shortDescriptionLength &&
            setAnswers({ ...answers, description: e.target.value })
          }
          InputProps={{
            endAdornment: activeError === requiredRefs.description && (
              <div className={classes.error}>
                <ErrorIcon color="error" />
                <span>This field is required</span>
              </div>
            )
          }}
        />
        <FormHelperText className={classes.helperText}>
          {answers.description.length}/{shortDescriptionLength}
        </FormHelperText>
      </FormControl>

      {maxBulletPoints > 0 && (
        <div style={{ display: 'grid' }}>
          <FormControl fullWidth={true} className={`${classes.formControl} ${classes.large}`}>
            <FormLabel className={classes.label}>Key Selling Points (Optional)</FormLabel>
            <span className={classes.subLabel}>
              Enter {maxBulletPoints} key selling points to attract candidates to view your role
            </span>
          </FormControl>
          {[...Array(maxBulletPoints).keys()].map((index) => (
            <FormControl
              fullWidth={true}
              className={`${classes.formControl} ${classes.large}`}
              key={index}
            >
              <TextField
                className={classes.textField}
                variant="outlined"
                value={answers.standout_bullets[index]}
                onChange={(e) =>
                  e.target.value.length <= 80 && setStandoutBullet(index, e.target.value)
                }
              />
              <FormHelperText className={classes.helperText}>
                {answers.standout_bullets[index] ? answers.standout_bullets[index].length : 0}/80
              </FormHelperText>
            </FormControl>
          ))}
        </div>
      )}

      {brandingEnabled && (
        <FormControl fullWidth={true} className={`${classes.formControl}`}>
          <FormLabel style={{ paddingBottom: '10px' }} className={classes.label}>
            Branding
          </FormLabel>
          {loadingSeekAdBranding ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Grid container style={{ margin: 0 }} spacing={2}>
              {getSeekAdBrandingData ? (
                getSeekAdBrandingData.map((brand) => {
                  const selected = answers.branding_id === brand.node.id.value;
                  const borderColor = selected ? '#5BC4BF' : '#e9e8e8';

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={4}
                      key={brand.node.id.value}
                      style={{
                        height: '230px',
                        maxWidth: '215px',
                        minWidth: '215px',
                        cursor: 'pointer',
                        border: `solid ${borderColor} 3px`,
                        borderRadius: '5px',
                        marginRight: '10px',
                        marginBottom: '10px',
                        padding: 0
                      }}
                    >
                      <RenderList brand={brand.node} setAnswers={setAnswers} answers={answers} />
                    </Grid>
                  );
                })
              ) : (
                <div>No Branding.</div>
              )}
              {getSeekAdBrandingData && seekBrandindInfo && (
                <div>
                  {seekBrandindInfo.hasNextPage ? (
                    <Button
                      disableElevation
                      className={classes.buttonSecondary}
                      style={{ minWidth: 130 }}
                      onClick={() => {
                        getSeekAdType(
                          `/api/v4/seek/job_ads/${jobAdId}/brands?after=${seekBrandindInfo.endCursor}`,
                          true
                        );
                      }}
                    >
                      Load More Branding
                    </Button>
                  ) : (
                    ''
                  )}
                </div>
              )}
            </Grid>
          )}
        </FormControl>
      )}

      <FormControl
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
        ref={requiredRefs.video_embed_code}
      >
        <FormLabel className={classes.label}>Video URL</FormLabel>
        <TextField
          className={classes.textField}
          error={activeError === requiredRefs.video_embed_code}
          variant="outlined"
          value={answers.video_embed_code}
          onChange={(e) =>
            e.target.value.length >= 0 &&
            setAnswers({ ...answers, video_embed_code: e.target.value })
          }
        />
        {activeError === requiredRefs.video_embed_code ? (
          <span>
            <div style={{ display: 'flex', marginTop: '0.5rem' }} className={classes.errorMessage}>
              <ErrorIcon color="error" />
              <div className="seekValidationErrorMsg">URL must start with https://</div>
            </div>
            <div style={{ display: 'flex', marginTop: '0.5rem' }} className={classes.errorMessage}>
              <ErrorIcon color="error" />
              <div className="seekValidationErrorMsg">
                Seek requires a YouTube link in one of the following formats:
              </div>
            </div>
            <div style={{ display: 'flex', marginTop: '0.5rem' }} className={classes.errorMessage}>
              <div className={classes.seekYoutubeUrl}>
                <div> https://www.youtube.com/embed/aAgePQvHBQM</div>
                <div style={{ padding: '0.25rem 0' }}>
                  {' '}
                  https://www.youtube.com/watch?v=aAgePQvHBQM
                </div>
                <div> https://youtu.be/aAgePQvHBQM</div>
              </div>
            </div>
          </span>
        ) : (
          <FormHelperText className={classes.helperText}>
            The URL you require will look like this: https://youtube.com/embed/abcd12345
          </FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.medium}`}>
        <FormLabel className={classes.label}>Video Position</FormLabel>
        <Grid container style={{ margin: 0 }} spacing={2}>
          <Grid style={{ marginRight: '10px' }}>
            <div
              id="seek-video-top"
              onClick={() => setAnswers({ ...answers, video_position: 'Above' })}
              style={{
                border: `solid ${answers.video_position === 'Above' ? '#5BC4BF' : '#e9e8e8'} 1px`,
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            />
          </Grid>
          <Grid>
            <div
              id="seek-video-bottom"
              onClick={() => setAnswers({ ...answers, video_position: 'Below' })}
              style={{
                border: `solid ${answers.video_position === 'Below' ? '#5BC4BF' : '#e9e8e8'} 1px`,
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            />
          </Grid>
        </Grid>
      </FormControl>
    </div>
  );
}

Step2.propTypes = {
  answers: PropTypes.object.isRequired,
  setAnswers: PropTypes.func.isRequired,
  jobAdId: PropTypes.number.isRequired,
  apiKey: PropTypes.string.isRequired,
  getSeekAdBrandingData: PropTypes.array || PropTypes.null,
  setGetSeekAdBrandingData: PropTypes.func.isRequired,
  requiredRefs: PropTypes.object.isRequired,
  activeError: PropTypes.ref,
  maxBulletPoints: PropTypes.number.isRequired,
  brandingEnabled: PropTypes.bool.isRequired
};

export default Step2;

const RenderList = ({ brand, setAnswers, answers }) => {
  const imageLength = brand.images.length;
  return (
    <div>
      {imageLength === 1 ? (
        <div>
          <div
            role="img"
            alt="Image not found"
            onClick={() => setAnswers({ ...answers, branding_id: brand.id.value })}
            style={
              brand.images[0].typeCode !== 'OriginalLogo'
                ? {
                    backgroundImage: `url(${brand.images[0].url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    height: '100px'
                  }
                : { height: '100px', padding: '10px' }
            }
          >
            {brand.images[0].typeCode === 'OriginalLogo' && 'No Cover Image'}
          </div>
          <div
            role="img"
            alt="Image not found"
            onClick={() => setAnswers({ ...answers, branding_id: brand.id.value })}
            style={
              brand.images[0].typeCode === 'OriginalLogo'
                ? {
                    backgroundImage: `url(${brand.images[0].url})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    padding: '0 0 0 5px',
                    height: '70px',
                    width: '100%'
                  }
                : { height: '100px', padding: '10px' }
            }
          >
            {brand.images[0].typeCode !== 'OriginalLogo' && 'No Logo Image'}
          </div>
        </div>
      ) : (
        <div>
          <div
            role="img"
            alt="Image not found"
            onClick={() => setAnswers({ ...answers, branding_id: brand.id.value })}
            style={{
              backgroundImage: `url(${brand.images[brand.images[0].typeCode === 'OriginalLogo' ? 1 : 0].url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              height: '100px'
            }}
          />
          <div
            role="img"
            alt="Image not found"
            onClick={() => setAnswers({ ...answers, branding_id: brand.id.value })}
            style={{
              backgroundImage: `url(${brand.images[brand.images[0].typeCode === 'OriginalLogo' ? 0 : 1].url})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              padding: '0 0 0 5px',
              height: '70px',
              width: '100%'
            }}
          />
        </div>
      )}
      <div>
        <div style={{ padding: '10px' }}>{brand.name}</div>
      </div>
    </div>
  );
};

RenderList.propTypes = {
  answers: PropTypes.object.isRequired,
  setAnswers: PropTypes.func.isRequired,
  brand: PropTypes.object
};
